import { Divider, Grid } from 'semantic-ui-react'
import { FormikProvider, useFormik } from 'formik'
import { DateTime } from 'luxon'

import {
  FormikDateInput,
  FormikLabelError,
  FormikRadioToggleButton,
  GridRowColumn,
  Text,
  makeDateSchema,
  makeReqStringSchema,
  getFieldNames,
  FormikScrollOnError,
} from '@/componentsV1/BaseComponents'
import { useReselector } from '@/utils/sharedHooks'
import { selectTaxListQuestionOptionByOptionId } from '@/features/taxes/annual-taxes/tax-checklist/taxChecklist.selectors'
import {
  TaxListOptionId,
  TaxListQuestionId,
  useTaxListQuestionRes,
} from '@/features/taxes/annual-taxes/tax-checklist/service'
import { selectCurrentAnnualTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { LifeChangesStepProps } from '.'
import { FormFlowFooter } from '@/components/form-flow/FormFlowFooter'
import { SubStepIdentifiers } from '@/features/taxes/annual-taxes/tax-checklist/shared/review-steps-and-progresses/stepProgress.helpers'
import UploadDocumentSubSection from '@/features/taxes/annual-taxes/tax-checklist/documents/UploadDocumentSubSection'
import { UserDocumentCategoryIdentifier } from '@/features/admin/user-document-categories/userDocumentCategory.constants'
import SkipQuestion, {
  createSkipSchema,
  skipQuestionId,
  useSkipQuestion,
} from '../../shared/SkipQuestion'
import { tqBigSpace, tqSmallSpace } from '../../helpers'
import { select1040FormForYear } from '../../../annualTaxFilingForms.selector'

export const purchasedHomeQuestionIds = [
  TaxListQuestionId.home_purchase_date,
  TaxListQuestionId.home_purchase_residence_type,
]

const PurchasedHomePanel = ({
  goBack,
  goToNextStep,
  previousScreen,
  nextScreen,
}: LifeChangesStepProps) => {
  const taxYear = useReselector(selectCurrentAnnualTaxYear)
  const form1040 = useReselector(select1040FormForYear, taxYear)
  const formId = form1040?.id

  const purchaseDateQR = useTaxListQuestionRes({
    questionId: TaxListQuestionId.home_purchase_date,
    formId,
  })

  const residenceTypeQR = useTaxListQuestionRes({
    questionId: TaxListQuestionId.home_purchase_residence_type,
    formId,
  })

  const residencyTypeOptionPrimary = useReselector(
    selectTaxListQuestionOptionByOptionId,
    TaxListQuestionId.home_purchase_residence_type,
    taxYear,
    TaxListOptionId.primary_residence
  )
  const residencyTypeOptionSecondary = useReselector(
    selectTaxListQuestionOptionByOptionId,
    TaxListQuestionId.home_purchase_residence_type,
    taxYear,
    TaxListOptionId.secondary_residence
  )
  const residencyTypeOptionRental = useReselector(
    selectTaxListQuestionOptionByOptionId,
    TaxListQuestionId.home_purchase_residence_type,
    taxYear,
    TaxListOptionId.rental_home
  )

  const skippedQuestions = useSkipQuestion(purchasedHomeQuestionIds)

  const formik = useFormik({
    initialValues: {
      [TaxListQuestionId.home_purchase_date]: purchaseDateQR.initialResponse,
      [TaxListQuestionId.home_purchase_residence_type]:
        residenceTypeQR.initialResponse,
      [skipQuestionId]: skippedQuestions.initialValue,
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      const responseData = [
        purchaseDateQR.getResponseData(values),
        residenceTypeQR.getResponseData(values),
      ]

      await goToNextStep(responseData, nextScreen ?? null, {
        completedSteps: [SubStepIdentifiers.purchasedHome],
      })
    },
  })

  const { values, isSubmitting, submitForm } = formik
  const fieldNames = getFieldNames(formik)

  return (
    <FormikProvider value={formik}>
      <FormikScrollOnError />
      <Grid>
        <GridRowColumn {...tqBigSpace}>
          <Text as="display2" textAlign="center">
            Purchased a Home
          </Text>
        </GridRowColumn>
        <GridRowColumn {...tqSmallSpace}>
          <FormikDateInput
            minDate={DateTime.fromISO(taxYear).startOf('year').toJSDate()}
            maxDate={DateTime.fromISO(taxYear).endOf('year').toJSDate()}
            fullWidth
            name={fieldNames[TaxListQuestionId.home_purchase_date]}
            label={purchaseDateQR.label}
            schema={createSkipSchema(
              values,
              TaxListQuestionId.home_purchase_date,
              makeDateSchema({
                field: 'date',
                format: DATE_FORMATS_LUXON.INPUT,
              })
            )}
          />
        </GridRowColumn>
        <GridRowColumn {...tqSmallSpace} short>
          <SkipQuestion questionId={TaxListQuestionId.home_purchase_date} />
        </GridRowColumn>
        <GridRowColumn {...tqSmallSpace}>
          <FormikLabelError
            name={fieldNames[TaxListQuestionId.home_purchase_residence_type]}
            label={residenceTypeQR.label}
            schema={createSkipSchema(
              values,
              TaxListQuestionId.home_sale_residence_type,
              makeReqStringSchema({ field: 'selection' })
            )}
          />
        </GridRowColumn>
        <GridRowColumn short {...tqSmallSpace}>
          <FormikRadioToggleButton
            fullWidth
            name={fieldNames[TaxListQuestionId.home_purchase_residence_type]}
            value={residencyTypeOptionPrimary?.id.toString() ?? ''}
          >
            {residencyTypeOptionPrimary?.text}
          </FormikRadioToggleButton>
        </GridRowColumn>
        <GridRowColumn short {...tqSmallSpace}>
          <FormikRadioToggleButton
            fullWidth
            name={fieldNames[TaxListQuestionId.home_purchase_residence_type]}
            value={residencyTypeOptionSecondary?.id.toString() ?? ''}
          >
            {residencyTypeOptionSecondary?.text}
          </FormikRadioToggleButton>
        </GridRowColumn>
        <GridRowColumn short {...tqSmallSpace}>
          <FormikRadioToggleButton
            fullWidth
            name={fieldNames[TaxListQuestionId.home_purchase_residence_type]}
            value={residencyTypeOptionRental?.id.toString() ?? ''}
          >
            {residencyTypeOptionRental?.text}
          </FormikRadioToggleButton>
        </GridRowColumn>
        <GridRowColumn {...tqSmallSpace} short>
          <SkipQuestion
            questionId={TaxListQuestionId.home_purchase_residence_type}
          />
        </GridRowColumn>
        <GridRowColumn short {...tqSmallSpace}>
          <Divider />
        </GridRowColumn>

        <UploadDocumentSubSection
          categories={[
            UserDocumentCategoryIdentifier.homePurchaseDocuments,
            values[TaxListQuestionId.home_purchase_residence_type] ===
            residencyTypeOptionRental?.id
              ? UserDocumentCategoryIdentifier.homeDepreciationSchedule
              : undefined,
          ]}
        />
        <Grid.Row />
        <FormFlowFooter
          continueDisabled={isSubmitting}
          onBack={() => goBack(previousScreen ?? null)}
          onForward={submitForm}
          loading={isSubmitting}
        />
      </Grid>
    </FormikProvider>
  )
}

export default PurchasedHomePanel
